import Phaser from 'phaser';

/** With / above weapon swing so shatter FX stays visible. */
const DEPTH = 14;
const MAX_PIECES = 28;
const SPARK_SCALE = 1.35;
const SPARK_KEY = 'spark-dot';

const BURST_TINT = [0xc084fc, 0xe879f9, 0xf0abfc, 0xa855f7, 0xffffff, 0xffe566];

type FlyingPiece = {
  obj: Phaser.GameObjects.Shape | Phaser.GameObjects.Image;
  vx: number;
  vy: number;
  gravity: number;
  life: number;
  maxLife: number;
  rot: number;
  vrot: number;
  initialScale: number;
  isMist?: boolean;
};

function rand(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function ensureSparkTexture(scene: Phaser.Scene) {
  if (scene.textures.exists(SPARK_KEY)) return;
  const g = scene.add.graphics();
  g.fillStyle(0xffffff, 1);
  g.fillCircle(6, 6, 6);
  g.generateTexture(SPARK_KEY, 12, 12);
  g.destroy();
}

export function createSmashParticles(scene: Phaser.Scene) {
  ensureSparkTexture(scene);

  let burstEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  const pieces: FlyingPiece[] = [];

  function getBurstEmitter() {
    if (burstEmitter?.active) return burstEmitter;
    burstEmitter = scene.add.particles(0, 0, SPARK_KEY, {
      lifespan: { min: 250, max: 650 },
      speed: { min: 80, max: 240 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.75 * SPARK_SCALE, end: 0 },
      alpha: { start: 1, end: 0 },
      gravityY: 120,
      tint: BURST_TINT,
      emitting: false,
      blendMode: Phaser.BlendModes.ADD,
    }).setDepth(DEPTH);
    return burstEmitter;
  }

  function trimPool() {
    while (pieces.length > MAX_PIECES) {
      pieces.shift()?.obj.destroy();
    }
  }

  function track(
    obj: Phaser.GameObjects.Shape | Phaser.GameObjects.Image,
    opts: Omit<FlyingPiece, 'obj'>,
  ) {
    trimPool();
    obj.setDepth(DEPTH);
    pieces.push({ obj, ...opts });
  }

  function spawnBurst(ox: number, oy: number, count = 8) {
    const em = getBurstEmitter();
    em.setPosition(ox, oy);
    em.explode(count + Math.floor(Math.random() * 4));
  }

  function spawnDustPuffs(ox: number, oy: number) {
    const puffCount = 3 + Math.floor(Math.random() * 3);
    for (let i = 0; i < puffCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = rand(25, 70);
      const circle = scene.add.circle(ox, oy, rand(14, 24), 0xa855f7, 0.35);
      track(circle, {
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        gravity: rand(-55, -25),
        life: rand(0.45, 0.85),
        maxLife: 0.85,
        rot: 0,
        vrot: 0,
        initialScale: 1,
        isMist: true,
      });
    }
  }

  function spawnDebrisPiece(ox: number, oy: number) {
    const id = 1 + Math.floor(Math.random() * 8);
    const size = rand(22, 48);
    const img = scene.add
      .image(ox, oy, `smash-piece-${id}`)
      .setDisplaySize(size, size)
      .setOrigin(0.5, 0.5);

    const angle = Math.random() * Math.PI * 2;
    const speed = rand(200, 480);
    track(img, {
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - rand(90, 220),
      gravity: rand(700, 1050),
      life: rand(0.55, 1.05),
      maxLife: 1.05,
      rot: rand(0, 360),
      vrot: rand(-320, 320),
      initialScale: rand(0.85, 1.15),
    });
  }

  function spawnAt(ox: number, oy: number, { heavy = false } = {}) {
    spawnBurst(ox, oy, heavy ? 14 : 8);
    spawnDustPuffs(ox, oy);

    if (!heavy && Math.random() > 0.5) return;

    const count = heavy ? 3 + Math.floor(Math.random() * 3) : 1 + Math.floor(Math.random() * 2);
    for (let i = 0; i < count; i++) {
      spawnDebrisPiece(ox, oy);
    }
  }

  function update(delta: number) {
    if (pieces.length === 0) return;
    const dt = Math.min(delta / 1000, 0.1);

    for (let i = pieces.length - 1; i >= 0; i--) {
      const p = pieces[i];
      p.life -= dt;
      if (p.life <= 0) {
        p.obj.destroy();
        pieces.splice(i, 1);
        continue;
      }

      p.vy += p.gravity * dt;
      const nx = p.obj.x + p.vx * dt;
      const ny = p.obj.y + p.vy * dt;
      p.obj.setPosition(nx, ny);
      p.rot += p.vrot * dt;

      const t = p.life / p.maxLife;
      const alpha = p.isMist ? t * 0.85 : Math.min(1, t * 1.2);
      p.obj.setAlpha(alpha);
      p.obj.setScale(p.initialScale * (0.4 + t * 0.6));
      if (p.obj instanceof Phaser.GameObjects.Image) {
        p.obj.setAngle(p.rot);
      }
    }
  }

  return { spawnAt, update };
}
